<template>
  <div class="d-grid gap-4">
    <div v-if="is_question_scope_report" class="row g-4">
      <div class="col-12 col-xl-6 d-flex report-builder-question-column">
        <div class="card border rounded-lg p-3 shadow-sm h-100 report-question-panel">
          <div class="mb-3">
            <label class="modal-form-label form-label text-xxs">
              <span class="badge bg-primary me-1">1</span> {{ translate_label("Form") }}
            </label>
            <div v-if="loading_forms" class="text-muted text-xxs py-2">
              {{ translate_label("Loading forms...") }}
            </div>
            <select
              v-else
              :value="selected_form_id"
              class="modal-form-select form-select text-xxs rounded-small p-2 shadow-sm text-x"
              @change="$emit('update:selected_form_id', $event.target.value)"
            >
              <option disabled value="">Select form</option>
              <option v-for="form in available_forms" :key="form.id" :value="form.id">
                {{ form.form_name }}
              </option>
            </select>
          </div>

          <div v-if="selected_form_id" class="mb-3">
            <label class="modal-form-label form-label text-xxs">
              <span class="badge bg-primary me-1">2</span> {{ translate_label("Main section") }}
            </label>
            <div v-if="loading_main_sections" class="text-muted text-xxs py-2">
              {{ translate_label("Loading sections...") }}
            </div>
            <select
              v-else
              :value="selected_main_section_id"
              class="modal-form-select form-select text-xxs rounded-small p-2 shadow-sm text-x"
              @change="$emit('update:selected_main_section_id', $event.target.value)"
            >
              <option disabled value="">Select main section</option>
              <option v-for="main_section in main_sections" :key="main_section.id" :value="main_section.id">
                {{ main_section.name }}
              </option>
            </select>
          </div>

          <div v-if="selected_main_section_id" class="mb-3">
            <label class="modal-form-label form-label text-xxs">
              <span class="badge bg-primary me-1">3</span> {{ translate_label("Sub-section") }}
            </label>
            <div v-if="loading_sub_sections" class="text-muted text-xxs py-2">
              {{ translate_label("Loading sub-sections...") }}
            </div>
            <select
              v-else
              :value="selected_sub_section_id"
              class="modal-form-select form-select text-xxs rounded-small p-2 shadow-sm text-x"
              @change="$emit('update:selected_sub_section_id', $event.target.value)"
            >
              <option disabled value="">Select sub-section</option>
              <option v-for="sub_section in sub_sections" :key="sub_section.id" :value="sub_section.id">
                {{ sub_section.name }}
              </option>
            </select>
          </div>

          <div v-if="selected_sub_section_id">
            <div class="d-flex justify-content-between align-items-center mb-2 gap-2">
              <label class="modal-form-label form-label text-xxs mb-0">
                <span class="badge bg-primary me-1">4</span> {{ translate_label("Questions") }}
              </label>
              <input
                :value="question_search"
                class="modal-form-control form-control form-control-sm text-xxs rounded-small p-2 shadow-sm"
                :placeholder="translate_label('Search questions...')"
                type="text"
                @input="$emit('update:question_search', $event.target.value)"
              />
            </div>

            <div v-if="loading_questions" class="text-muted text-xxs py-2">
              {{ translate_label("Loading questions...") }}
            </div>

            <div v-else-if="filtered_questions.length" class="scrollable-question-list">
              <label
                v-for="question in filtered_questions"
                :key="question.id"
                class="card border rounded-lg p-3 shadow-sm mb-2 d-block"
                :class="{ 'border-primary': is_question_selected(question.id) }"
              >
                <div class="d-flex align-items-start gap-2">
                  <input
                    :checked="is_question_selected(question.id)"
                    class="form-check-input mt-1 shadow-sm"
                    type="checkbox"
                    @change="$emit('toggle-question-selection', question)"
                  />
                  <div class="flex-grow-1">
                    <p class="fw-semibold text-dark text-xxs mb-1">{{ question.question_text }}</p>
                    <p class="text-muted text-xxs mb-0">
                      {{ question.form_name }} | {{ question.section_name }} | {{ format_question_type(question.question_type) }}
                    </p>
                  </div>
                </div>
              </label>
            </div>

            <p v-else class="text-muted text-xxs mb-0">
              {{ translate_label("No questions found in this sub-section.") }}
            </p>
          </div>
        </div>
      </div>

      <div class="col-12 col-xl-6 d-flex report-builder-question-column">
        <div class="card border rounded-lg p-3 shadow-sm h-100 report-question-panel">
          <div class="d-flex flex-column flex-lg-row justify-content-between align-items-lg-center gap-2 mb-3">
            <div>
              <h5 class="text-x fw-bold mb-1">{{ translate_label("Selected questions") }}</h5>
              <p class="text-muted text-xxs mb-0">{{ selected_questions_description }}</p>
            </div>
            <span class="text-uppercase text-muted text-xxs">
              {{ selected_questions.length }} {{ translate_label("chosen") }}
            </span>
          </div>

          <div v-if="selected_questions.length" class="scrollable-selected-questions">
            <div
              v-for="(question, selected_index) in selected_questions"
              :key="question.id"
              class="card border rounded-lg p-3 shadow-sm mb-3"
              :class="{ 'report-selected-question-draggable': allow_selected_question_reorder }"
              :draggable="allow_selected_question_reorder"
              @dragstart="handle_selected_question_drag_start($event, selected_index)"
              @dragover.prevent="handle_selected_question_drag_over($event)"
              @drop.prevent="handle_selected_question_drop(selected_index)"
              @dragend="handle_selected_question_drag_end"
            >
              <div class="d-flex justify-content-between align-items-start gap-2">
                <div class="d-flex align-items-start gap-2">
                  <span
                    v-if="allow_selected_question_reorder"
                    class="text-muted report-selected-question-handle"
                  >
                    <i class="fas fa-grip-vertical"></i>
                  </span>
                  <div>
                    <p class="fw-semibold text-dark text-xxs mb-1">{{ question.question_text }}</p>
                    <p class="text-muted text-xxs mb-0">
                      {{ question.form_name }} | {{ question.section_name }} | {{ format_question_type(question.question_type) }}
                    </p>
                  </div>
                </div>
                <button
                  class="btn btn-primary-dark btn-sm rounded-small text-xxs shadow text-dark"
                  type="button"
                  @click="$emit('remove-question', question.id)"
                >
                  {{ translate_label("Remove") }}
                </button>
              </div>

              <p
                v-if="allow_selected_question_reorder"
                class="text-muted text-xxs mb-0 mt-2"
              >
                Drag to reorder how these linked questions appear in the PDF section.
              </p>

              <div v-if="is_select_type(question)" class="mt-3">
                <div v-if="is_question_statistics_summary" class="mb-3">
                  <label class="modal-form-label form-label text-xxs">{{ translate_label("Statistics") }}</label>
                  <div class="row g-2">
                    <div
                      v-for="metric_type in get_allowed_metric_types(question)"
                      :key="metric_type.id"
                      class="col-12 col-md-6"
                    >
                      <label
                        class="card border rounded-lg p-2 shadow-sm h-100"
                        :class="{ 'border-primary': (question.selected_metric_type_ids || []).includes(metric_type.id) }"
                      >
                        <div class="d-flex align-items-center gap-2">
                          <input
                            :checked="(question.selected_metric_type_ids || []).includes(metric_type.id)"
                            class="form-check-input shadow-sm"
                            type="checkbox"
                            @change="$emit('toggle-question-metric', { question_id: question.id, metric_type_id: metric_type.id })"
                          />
                          <span class="fw-semibold text-dark text-xxs">{{ metric_type.display_name }}</span>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
                <label class="modal-form-label form-label text-xxs">{{ translate_label("Optional answer filter") }}</label>
                <select
                  v-model="question.selected_values"
                  class="modal-form-select form-select text-xxs rounded-small p-2 shadow-sm text-x"
                  multiple
                >
                  <option v-for="choice in question.choices" :key="choice.id" :value="choice.choice">
                    {{ choice.choice }}
                  </option>
                </select>
                <p class="text-muted text-xxs mt-2 mb-0">
                  {{ translate_label("Leave this empty to match any answer. Select multiple values when the report should filter to any of them.") }}
                </p>
              </div>

              <div v-else-if="is_question_statistics_summary" class="mt-3">
                <label class="modal-form-label form-label text-xxs">{{ translate_label("Statistics") }}</label>
                <div class="row g-2">
                  <div
                    v-for="metric_type in get_allowed_metric_types(question)"
                    :key="metric_type.id"
                    class="col-12 col-md-6"
                  >
                    <label
                      class="card border rounded-lg p-2 shadow-sm h-100"
                      :class="{ 'border-primary': (question.selected_metric_type_ids || []).includes(metric_type.id) }"
                    >
                      <div class="d-flex align-items-center gap-2">
                        <input
                          :checked="(question.selected_metric_type_ids || []).includes(metric_type.id)"
                          class="form-check-input shadow-sm"
                          type="checkbox"
                          @change="$emit('toggle-question-metric', { question_id: question.id, metric_type_id: metric_type.id })"
                        />
                        <span class="fw-semibold text-dark text-xxs">{{ metric_type.display_name }}</span>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="card border rounded-lg p-3 shadow-sm bg-light">
            <p class="fw-bold text-dark text-xxs mb-1">{{ empty_selected_questions_title }}</p>
            <p class="text-muted text-xxs mb-0">{{ empty_selected_questions_description }}</p>
          </div>
        </div>
      </div>
    </div>

    <div v-else class="card border rounded-lg p-3 shadow-sm bg-light">
      <p class="fw-bold text-dark text-xxs mb-1">{{ translate_label("Summary grouping") }}</p>
      <p class="text-muted text-xxs mb-0">{{ non_question_scope_summary_description }}</p>
    </div>
  </div>
</template>

<script>
export default {
  name: "report_builder_data_selection_step",
  props: {
    available_forms: { type: Array, required: true },
    empty_selected_questions_description: { type: String, required: true },
    empty_selected_questions_title: { type: String, required: true },
    filtered_questions: { type: Array, required: true },
    format_question_type: { type: Function, required: true },
    get_allowed_metric_types: { type: Function, required: true },
    allow_selected_question_reorder: { type: Boolean, default: false },
    is_question_scope_report: { type: Boolean, required: true },
    is_question_selected: { type: Function, required: true },
    is_question_statistics_summary: { type: Boolean, required: true },
    is_select_type: { type: Function, required: true },
    loading_forms: { type: Boolean, required: true },
    loading_main_sections: { type: Boolean, required: true },
    loading_questions: { type: Boolean, required: true },
    loading_sub_sections: { type: Boolean, required: true },
    main_sections: { type: Array, required: true },
    non_question_scope_summary_description: { type: String, required: true },
    question_search: { type: String, required: true },
    selected_form_id: { type: [Number, String], required: true },
    selected_main_section_id: { type: [Number, String], required: true },
    selected_questions: { type: Array, required: true },
    selected_questions_description: { type: String, required: true },
    selected_sub_section_id: { type: [Number, String], required: true },
    sub_sections: { type: Array, required: true },
    translate_label: { type: Function, required: true },
  },
  emits: [
    "remove-question",
    "reorder-selected-question",
    "toggle-question-metric",
    "toggle-question-selection",
    "update:question_search",
    "update:selected_form_id",
    "update:selected_main_section_id",
    "update:selected_sub_section_id",
  ],
  data: () => ({
    dragged_selected_question_index: null,
  }),
  methods: {
    handle_selected_question_drag_start(event, selected_index) {
      if (!this.allow_selected_question_reorder) {
        return;
      }
      this.dragged_selected_question_index = selected_index;
      if (event?.dataTransfer) {
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", String(selected_index));
      }
    },
    handle_selected_question_drag_over(event) {
      if (!this.allow_selected_question_reorder || !event?.dataTransfer) {
        return;
      }
      event.dataTransfer.dropEffect = "move";
    },
    handle_selected_question_drop(target_index) {
      const source_index = this.dragged_selected_question_index;
      this.dragged_selected_question_index = null;
      if (source_index === null || source_index === target_index) {
        return;
      }
      this.$emit("reorder-selected-question", { source_index, target_index });
    },
    handle_selected_question_drag_end() {
      this.dragged_selected_question_index = null;
    },
  },
};
</script>

<style scoped>
.report-builder-question-column {
  min-height: 0;
}

.report-question-panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-height: 0;
  overflow: hidden;
}

.scrollable-question-list,
.scrollable-selected-questions {
  flex: 1 1 auto;
  max-height: 28rem;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 0.25rem;
  position: relative;
  z-index: 1;
}

.report-selected-question-draggable {
  cursor: move;
}

.report-selected-question-handle {
  line-height: 1;
  padding-top: 0.125rem;
}

@media (max-width: 991.98px) {
  .scrollable-question-list,
  .scrollable-selected-questions {
    max-height: 20rem;
  }
}
</style>
